import { useEffect, useMemo, useRef, useState } from "react"

import { useFetcher, useSearchParams } from "react-router-dom"

type LoadingUIOptions = {
  /** How long to wait before showing the loading UI (prevents flicker). */
  delayMs?: number
  /** Once shown, keep the loading UI visible at least this long. */
  minDurationMs?: number
}

export type Status = "loading" | "error" | "noData" | "success"

export function useInsightPanelDataFetcher<TData>(
  path: string,
  { delayMs = 200, minDurationMs = 300 }: LoadingUIOptions = {}
) {
  const fetcher = useFetcher<{ data: TData }>()
  const [searchParams] = useSearchParams()

  // TODO: determine how we want to get override dates appended to the individual panel queries and set them here.
  // const globalStart = searchParams.get("q[start_date]") ?? undefined
  // const globalEnd = searchParams.get("q[end_date]") ?? undefined

  // const overrideStartDate = undefined
  // const overrideEndDate = undefined

  // const effectiveStartDate = overrideStartDate ?? globalStart
  // const effectiveEndDate = overrideEndDate ?? globalEnd

  const url = useMemo(() => {
    const queryString = searchParams.toString()

    // Always include the user's timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

    return queryString ? `${path}?${queryString}&timezone=${timezone}` : path
  }, [path, searchParams])

  useEffect(() => {
    fetcher.load(url)
  }, [url])

  const isLoadingRaw = fetcher.state === "loading"

  const [isLoading, setIsLoading] = useState(false)
  const showLoadStateTimerRef = useRef<number | null>(null)
  const hideLoadStateTimerRef = useRef<number | null>(null)
  const shownAtRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (showLoadStateTimerRef.current) window.clearTimeout(showLoadStateTimerRef.current)
      if (hideLoadStateTimerRef.current) window.clearTimeout(hideLoadStateTimerRef.current)
    }
  }, [])

  useEffect(() => {
    // Always clear scheduled hides on transition
    if (hideLoadStateTimerRef.current) {
      window.clearTimeout(hideLoadStateTimerRef.current)
      hideLoadStateTimerRef.current = null
    }

    if (isLoadingRaw) {
      if (isLoading) return

      // (Re)start the delay before showing load state
      if (showLoadStateTimerRef.current) window.clearTimeout(showLoadStateTimerRef.current)
      showLoadStateTimerRef.current = window.setTimeout(() => {
        shownAtRef.current = Date.now()
        setIsLoading(true)
      }, delayMs)
    } else {
      // Not loading anymore
      // Cancel any pending load state timer
      if (showLoadStateTimerRef.current) {
        window.clearTimeout(showLoadStateTimerRef.current)
        showLoadStateTimerRef.current = null
      }

      if (isLoading) {
        // Enforce a minimum visible time to prevent load state flicker
        const elapsed = shownAtRef.current ? Date.now() - shownAtRef.current : minDurationMs
        const remaining = Math.max(0, minDurationMs - elapsed)

        if (remaining === 0) {
          setIsLoading(false)
          shownAtRef.current = null
        } else {
          hideLoadStateTimerRef.current = window.setTimeout(() => {
            setIsLoading(false)
            shownAtRef.current = null
          }, remaining)
        }
      } else {
        // Never showed; ensure clean state
        setIsLoading(false)
        shownAtRef.current = null
      }
    }
  }, [isLoadingRaw, isLoading, delayMs, minDurationMs])

  return {
    data: fetcher.data as TData | undefined,
    isLoading,
    isLoadingRaw,
  }
}
